import axios from 'axios';
import oauth from 'axios-oauth-client';
import tokenProvider from 'axios-token-interceptor';
import {store as downloadStore} from 'components/Downloader';
import * as firebase from 'firebase/app';
import 'firebase/messaging';
import pRetry from 'p-retry';
import {queryCache} from 'react-query';
import {FORM_ERROR} from 'final-form';
import {application, production} from 'configs/app';
import store from 'store';
import SnackbarUtil from 'utils/snackbar';
import {hideSignboard} from '../views/extera/signboard';

function getMaxAge(res) {
  return res.expires_in * 1000;
}

function headerFormatter(res) {
  return 'Bearer ' + res.access_token;
}

class Api {
  _cachedToken;

  logout = () => {
    localStorage.clear();
    this.clearCachedToken();
    store.getActions().auth.logout();
    queryCache.clear();
    downloadStore.clear();

    if (firebase.apps.length) {
      const messaging = firebase.messaging();
      messaging.deleteToken();
    }
    hideSignboard();
  };

  /**
   * Oauth password grant
   * @param {string} username
   * @param {string} password
   * @return {Promise<object>}
   */
  passwordGrant = (username, password) => {
    return pRetry(
      async () => {
        try {
          const getOwnerCredentials = await oauth.client(
            axios.create({
              timeout: 60000,
            }),
            {
              url: application.endpoint + application.tokenPath,
              grant_type: 'password',
              client_id: application.id,
              username,
              password,
            },
          );
          const response = await getOwnerCredentials();

          localStorage.setItem('refreshToken', response.refresh_token);

          this._cachedToken = tokenProvider.tokenCache(
            () => Promise.resolve(response),
            {
              getMaxAge,
            },
          );

          store.getActions().auth.login();

          return response;
        } catch (e) {
          if (e.response && Math.floor(e.response.status / 100) === 4) {
            throw new pRetry.AbortError(e);
          } else if (!e.request) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      },
    );
  };

  /**
   * Oauth refresh token
   * @return {Promise<object>}
   */
  refreshToken = (refreshToken) => {
    return pRetry(
      async () => {
        if (!refreshToken && !localStorage.getItem('refreshToken')) {
          const e = new Error('Refresh token not found');
          e.suppressEmitNotification = true;
          this.clearCachedToken();
          throw new pRetry.AbortError(e);
        }

        try {
          if (!refreshToken) {
            refreshToken = localStorage.getItem('refreshToken');
          }

          const getRefreshToken = oauth.client(
            axios.create({
              timeout: 10000,
            }),
            {
              url: application.endpoint + application.tokenPath,
              grant_type: 'refresh_token',
              client_id: application.id,
              refresh_token: refreshToken,
            },
          );

          const response = await getRefreshToken();

          if (response.refresh_token || refreshToken) {
            localStorage.setItem(
              'refreshToken',
              response.refresh_token || refreshToken,
            );
          }

          return response;
        } catch (e) {
          if (e.response && Math.floor(e.response.status / 100) === 4) {
            this.logout();
            throw new pRetry.AbortError(e);
          } else if (!e.request) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: 3,
        factor: 2.4477,
      },
    );
  };

  getCachedToken = () => {
    if (!this._cachedToken) {
      this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
        getMaxAge,
      });
    }
    return this._cachedToken();
  };

  resetCachedToken = () => {
    if (this._cachedToken) {
      this._cachedToken.reset();
    }
  };

  clearCachedToken = () => {
    this._cachedToken = undefined;
  };

  /**
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams|FormData|File|Blob} data
   * @param {object} options
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   * @private
   */
  _invoke = async (method, path, data, options) => {
    const instance = axios.create({
      method,
      baseURL: application.endpoint,
      url: path,
      headers: {
        'Accept-Language': 'fa-IR, en;q=0.8, *;q=0.5',
        'Cache-Control': 'no-cache',
      },
      timeout:
        data instanceof FormData ||
        (options && options.responseType === 'arraybuffer')
          ? 0
          : 10000,
    });

    if (this._cachedToken) {
      instance.interceptors.request.use(
        tokenProvider({
          getToken: this._cachedToken,
          headerFormatter,
        }),
      );
    } else {
      const hasRefreshToken = !!localStorage.getItem('refreshToken');
      if (hasRefreshToken) {
        if (!this._cachedToken) {
          this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
            getMaxAge,
          });
        }
        instance.interceptors.request.use(
          tokenProvider({
            getToken: this._cachedToken,
            headerFormatter,
          }),
        );
      }
    }

    try {
      return await instance.request({data, ...options});
    } catch (e) {
      if (e.response && e.response.status === 401) {
        this._cachedToken = tokenProvider.tokenCache(this.refreshToken, {
          getMaxAge,
        });
        return this._invoke(method, path, data, options);
      } else if (!e.request) {
        throw new pRetry.AbortError(e);
      }
      throw e;
    }
  };

  /**
   * Invoke API method
   * @param {string} method
   * @param {string} path
   * @param {string|object|ArrayBuffer|ArrayBufferView|URLSearchParams} [data]
   * @param {object} [options]
   * @return {Promise<{data:*,status:number,headers:object,config:object,request:XMLHttpRequest}>}
   */
  invoke = (method, path, data, options) => {
    const apiOptions = {
      emitNotification: false,
      retries: method.toUpperCase() === 'GET' ? 3 : 0,
      ...((options && options.api) || {}),
    };

    if (options) {
      delete options.api;
    }
    return pRetry(
      async () => {
        try {
          return await this._invoke(method, path, data, options);
        } catch (e) {
          if (e.response && Math.floor(e.response.status / 100) === 4) {
            throw new pRetry.AbortError(e);
          }
          throw e;
        }
      },
      {
        retries: apiOptions.retries,
        factor: 2.4477,
      },
    ).catch((e) => {
      let message;
      let data;

      if (e.response) {
        console.log({production});
        if (e.response.status === 500 && production) {
          // window.location.replace(`${window.location.origin}/serverdown`);
        } else if (e.response.data instanceof ArrayBuffer) {
          if (e.response.data.byteLength < 10240) {
            try {
              const buffer = new Buffer(e.response.data, 'binary');
              data = JSON.parse(buffer.toString());
            } catch (e) {
              console.error(e);
            }
          }
        } else {
          data = e.response.data;
        }
      }

      if ((data && data.message) || data?.msg) {
        message = data.message || data?.msg;
      } else if (data && data.error === 'invalid_grant') {
        message = null;
      } else if (e.request) {
        message = '311دسترسی به اینترنت خود را بررسی و مجددا تلاش نمایید';
      } else {
        message = 'خطایی رخ داده است';
      }

      if (
        message &&
        apiOptions.emitNotification &&
        !e.suppressEmitNotification
      ) {
        SnackbarUtil.error(message);
      }

      throw new ApiError(data, e, message);
    });
  };
}

export class ApiError extends Error {
  constructor(data, e, ...params) {
    super(...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }
    this.name = 'ApiError';
    this.code = data?.name || data?.error || 'UNKNOWN';
    this.data = data;

    if (!this.message) {
      this.message = 'خطایی رخ داده است';
    }
    this.e = e;

    if (data) {
      if (data.details) {
        this.fieldError = data.details.reduce((obj, item) => {
          const field = item['field'].split('/')[0];
          if (!obj.hasOwnProperty(field)) {
            obj[field] = item['issue'];
          }
          return obj;
        }, {});
      } else if (data.message) {
        this.fieldError = {
          [FORM_ERROR]: data.message,
        };
      }
    } else if (e.request) {
      this.fieldError = {
        [FORM_ERROR]:
          '340لطفا دقایقی دیگر مجدد تلاش کنید یا اینترنت خود را بررسی نمایید ',
      };
    }

    if (!this.fieldError) {
      this.fieldError = {
        [FORM_ERROR]: 'خطایی رخ داده است',
      };
    }
  }
}

const api = new Api();
api.Error = ApiError;

export default api;
